import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TripExpensesList } from '../components/TripExpensesList';
import { TripStatsCard } from '../components/TripStatsCard';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { StatusPill } from '../components/ui/StatusPill';
import { TextField } from '../components/ui/TextField';
import { useTrip } from '../hooks/useTrip';
import { type TFunction, useT } from '../i18n';
import { finalizarTrip, prestarTrip } from '../lib/api';
import { formatMoney, sumMoney } from '../lib/money';

const STATUS_HINT_KEYS = new Set([
  'rascunho',
  'aguardando_aprovacao',
  'finalizada',
  'em_prestacao',
  'encerrada',
  'rejeitada',
]);

function TripLoading() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Spinner className="h-6 w-6 text-accent" />
    </div>
  );
}

function TripNotFound({
  error,
  refreshing,
  onRetry,
  t,
}: {
  error: string | undefined;
  refreshing: boolean;
  onRetry: () => void;
  t: TFunction;
}) {
  return (
    <div className="flex flex-1 flex-col gap-4 px-4 py-5">
      <Link to="/viagens" className="text-sm text-ink-muted">
        ← {t('trips.backToTrips')}
      </Link>
      <p className="text-sm text-danger">{error ?? t('trips.notFound')}</p>
      <Button variant="secondary" onClick={onRetry} loading={refreshing}>
        {t('common.retry')}
      </Button>
    </div>
  );
}

export function TripDetailPage() {
  const { t } = useT();
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { trip, loading, pending, error, refreshing, refresh } = useTrip(tripId);
  const [actionError, setActionError] = useState<string>();
  const [actionLoading, setActionLoading] = useState(false);
  const [odometroOpen, setOdometroOpen] = useState(false);
  const [odometroFim, setOdometroFim] = useState('');
  const [odometroError, setOdometroError] = useState<string>();

  if (loading) return <TripLoading />;
  if (!trip) {
    return (
      <TripNotFound error={error} refreshing={refreshing} onRetry={() => void refresh()} t={t} />
    );
  }

  const canAddExpense = trip.status === 'aprovada' || trip.status === 'em_andamento';
  const canFinalizar = trip.status === 'em_andamento';
  const canPrestar = trip.status === 'finalizada';
  const isCarro = trip.tipoLocomocao === 'carro';
  const activePending = pending.filter((item) => item.status !== 'synced');
  const pendingTotal = sumMoney(activePending.map((item) => item.valor));

  async function runAction(action: () => Promise<unknown>): Promise<void> {
    setActionLoading(true);
    setActionError(undefined);
    try {
      await action();
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('trips.actionError'));
    } finally {
      setActionLoading(false);
    }
  }

  /** Viagem de carro pede o odômetro de chegada num modal; os demais finalizam direto. */
  function handleFinalizar(): void {
    if (!trip) return;
    if (isCarro) {
      setOdometroFim('');
      setOdometroError(undefined);
      setOdometroOpen(true);
      return;
    }
    void runAction(() => finalizarTrip(trip.id));
  }

  async function confirmFinalizar(): Promise<void> {
    if (!trip) return;
    const raw = odometroFim.trim();
    let payload: { odometroFim?: number } = {};
    if (raw.length > 0) {
      const parsed = Number(raw);
      if (!Number.isInteger(parsed) || parsed < 0) {
        setOdometroError(t('trips.odometro.invalid'));
        return;
      }
      if (trip.odometroInicio != null && parsed < trip.odometroInicio) {
        setOdometroError(t('trips.odometro.tooLow', { min: trip.odometroInicio }));
        return;
      }
      payload = { odometroFim: parsed };
    }
    setOdometroOpen(false);
    await runAction(() => finalizarTrip(trip.id, payload));
  }

  return (
    <div className="flex flex-1 flex-col gap-4 px-4 py-5 pb-28">
      <div className="flex items-center justify-between">
        <Link to="/viagens" className="text-sm text-ink-muted">
          ← {t('trips.backToTrips')}
        </Link>
        <StatusPill tone="accent">{t(`enums.tripStatus.${trip.status}`)}</StatusPill>
      </div>

      <div>
        <h1 className="font-display text-xl font-semibold text-ink">
          {trip.origem} → {trip.destino}
        </h1>
        <p className="text-sm text-ink-muted">
          {t(trip.dias === 1 ? 'trips.days' : 'trips.daysPlural', { n: trip.dias })} ·{' '}
          {t('trips.custeioLabel')} {t(`enums.formaCusteio.${trip.formaCusteio}`)} ·{' '}
          {t(`enums.tipoLocomocao.${trip.tipoLocomocao}`)}
        </p>
        {isCarro && trip.kmRodado != null && (
          <p className="text-sm text-ink-muted">{t('trips.kmRodado', { km: trip.kmRodado })}</p>
        )}
      </div>

      <TripStatsCard
        teto={trip.teto}
        totalGasto={trip.totalGasto}
        sobra={trip.sobra}
        moeda={trip.moeda}
      />

      {Number(pendingTotal) > 0 && (
        <p className="text-sm text-ink-muted">
          +{' '}
          <span className="tabular-money text-accent">{formatMoney(pendingTotal, trip.moeda)}</span>{' '}
          {t('trips.pendingSync')}
        </p>
      )}

      {!canAddExpense && (
        <p className="rounded-lg bg-surface-raised px-3 py-2 text-sm text-ink-muted">
          {STATUS_HINT_KEYS.has(trip.status)
            ? t(`trips.statusHint.${trip.status}`)
            : t('trips.cannotAddExpense')}
        </p>
      )}

      {actionError && <p className="text-sm text-danger">{actionError}</p>}

      <div className="flex gap-2">
        {canFinalizar && (
          <Button variant="secondary" fullWidth loading={actionLoading} onClick={handleFinalizar}>
            {t('trips.finalizar')}
          </Button>
        )}
        {canPrestar && (
          <Button
            variant="secondary"
            fullWidth
            loading={actionLoading}
            onClick={() => void runAction(() => prestarTrip(trip.id))}
          >
            {t('trips.prestarContas')}
          </Button>
        )}
      </div>

      <div>
        <h2 className="mb-2 font-display text-sm font-semibold uppercase tracking-wide text-ink-muted">
          {t('trips.expensesTitle')}
        </h2>
        <TripExpensesList expenses={trip.expenses} pending={activePending} />
      </div>

      {canAddExpense && (
        <div className="safe-bottom fixed inset-x-0 bottom-0 border-t border-line bg-bg px-4 py-3">
          <Button fullWidth onClick={() => navigate(`/viagens/${trip.id}/novo-gasto`)}>
            {t('trips.addExpense')}
          </Button>
        </div>
      )}

      {odometroOpen && (
        <div className="fixed inset-0 z-20 flex items-end justify-center bg-black/50 px-4 py-6">
          <div className="w-full max-w-md rounded-xl border border-line bg-bg p-4">
            <h2 className="font-display text-lg font-semibold text-ink">
              {t('trips.odometro.title')}
            </h2>
            <p className="mt-1 text-sm text-ink-muted">{t('trips.odometro.subtitle')}</p>
            <div className="mt-4">
              <TextField
                label={t('trips.odometro.label')}
                inputMode="numeric"
                placeholder="0"
                mono
                value={odometroFim}
                onChange={(event) => setOdometroFim(event.target.value)}
                error={odometroError}
                autoFocus
              />
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                variant="ghost"
                fullWidth
                onClick={() => setOdometroOpen(false)}
                disabled={actionLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button fullWidth loading={actionLoading} onClick={() => void confirmFinalizar()}>
                {t('trips.finalizar')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
